/**
 * Cache Service
 *
 * API service for cache management and refresh operations.
 * Connects to FastAPI /api/cache endpoints.
 */

import { apiClient } from './apiClient';
import { apiCall } from '@shared/utils/apiWrapper';
import type {
  CacheStatusResponse,
  CacheRefreshRequest,
  CacheRefreshResponse,
} from '../../../types/api';

/**
 * Get cache status
 *
 * @returns Promise resolving to cache status information
 *
 * @example
 * ```typescript
 * const status = await getCacheStatus();
 * console.log(`Last refresh: ${status.last_refresh}`);
 * console.log(`Organizations: ${status.organizations_count}`);
 * console.log(`Contacts: ${status.contacts_count}`);
 * console.log(`Refreshing: ${status.is_refreshing}`);
 * ```
 */
export async function getCacheStatus(): Promise<CacheStatusResponse> {
  return apiCall('get cache status', () =>
    apiClient.get<CacheStatusResponse>('/cache/status')
  );
}

/**
 * Trigger cache refresh
 *
 * Initiates a background cache refresh from Pipedrive.
 * Use SSE to monitor progress in real-time.
 *
 * @param force - Force refresh even if cache is recent (default: false)
 * @returns Promise resolving to refresh response
 *
 * @example
 * ```typescript
 * const result = await refreshCache({ force: true });
 * console.log(result.message);
 * console.log(`Started at: ${result.started_at}`);
 *
 * // Monitor progress via SSE
 * const eventSource = new EventSource('/api/cache/refresh');
 * eventSource.onmessage = (event) => {
 *   const data = JSON.parse(event.data);
 *   console.log(`Progress: ${data.progress}%`);
 * };
 * ```
 */
export async function refreshCache(
  request: CacheRefreshRequest = {}
): Promise<CacheRefreshResponse> {
  return apiCall('refresh cache', () =>
    apiClient.post<CacheRefreshResponse>('/cache/refresh', request)
  );
}

/**
 * Check if cache is currently refreshing
 *
 * @returns Promise resolving to boolean indicating refresh status
 *
 * @example
 * ```typescript
 * const isRefreshing = await isCacheRefreshing();
 * if (isRefreshing) {
 *   console.log('Cache refresh in progress...');
 * }
 * ```
 */
export async function isCacheRefreshing(): Promise<boolean> {
  try {
    const status = await getCacheStatus();
    return status.is_refreshing;
  } catch (error) {
    console.error('Failed to check cache refresh status:', error);
    return false;
  }
}
